import { ChevronDown, ChevronRight } from "lucide-react";
import clsx from "clsx";
import Image from "next/image";

import { Asset, Folder, TreeNode } from "../types";
import { buildAssetUrl } from "../utils";
import { AssetPreview } from "./AssetPreview";

type ExplorerTreeProps = {
  tree: Record<string, TreeNode>;
  rootId: string | null;
  currentFolderId: string | null;
  selectedAssetId: string | null;
  loading?: boolean;
  onFolderClick: (id: string) => void;
  onToggleNode: (folder: Folder) => void;
  onAssetClick: (asset: Asset) => void;
  assetFilter?: (asset: Asset) => boolean;
};

export function ExplorerTree({
  tree,
  rootId,
  currentFolderId,
  selectedAssetId,
  loading,
  onFolderClick,
  onToggleNode,
  onAssetClick,
  assetFilter,
}: ExplorerTreeProps) {
  const renderNode = (folderId: string | null) => {
    if (!folderId) return null;
    const node = tree[folderId];
    if (!node) return null;
    const isCurrent = currentFolderId === folderId;

    return (
      <div key={folderId} className="space-y-1">
        <div className="flex items-center gap-2 rounded px-2 py-1">
          <button
            className="text-[10px] text-gray-500"
            onClick={() => onToggleNode(node.folder)}
            aria-label={node.open ? "Collapse" : "Expand"}
          >
            {node.open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <button
            className={clsx(
              "flex flex-1 items-center gap-2 rounded px-2 py-1 text-left",
              isCurrent
                ? "bg-pinkCustom/10 text-pinkCustom"
                : "hover:bg-pinkCustom/5",
            )}
            onClick={() => onFolderClick(node.folder.id)}
          >
            {node.folder.previewAsset ? (
              <Image
                src={buildAssetUrl(node.folder.previewAsset)}
                alt=""
                width={22}
                height={22}
                className="h-[22px] w-[22px] rounded object-cover"
                unoptimized
              />
            ) : (
              <span>📁</span>
            )}
            <span className="truncate">{node.folder.name}</span>
          </button>
        </div>
        {node.open ? (
          <div className="space-y-1 pl-4">
            {node.childrenIds.map((childId) => renderNode(childId))}
            {node.assets
              .filter((asset) => (assetFilter ? assetFilter(asset) : true))
              .map((asset) => {
                const isSelected = selectedAssetId === asset.id;
                return (
                  <button
                    key={asset.id}
                    className={clsx(
                      "flex w-full items-center gap-2 rounded px-2 py-1 text-left",
                      isSelected
                        ? "bg-pinkCustom/10 text-pinkCustom"
                        : "hover:bg-pinkCustom/5",
                    )}
                    onClick={() => onAssetClick(asset)}
                  >
                    <AssetPreview asset={asset} size={22} />
                    <span className="truncate text-xs">
                      {asset.originalFilename}
                    </span>
                  </button>
                );
              })}
            {node.loading ? (
              <div className="px-2 py-1 text-[11px] text-gray-500">
                Loading...
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-1 text-xs text-gray-700">
      {loading ? <div className="text-gray-500">Loading tree...</div> : null}
      {!loading && rootId ? renderNode(rootId) : null}
    </div>
  );
}
